import { ApolloCache, gql } from '@apollo/client';
import { categoryFragment } from '../fragments/category';
import { goalFragment } from '../fragments/goal';
import { questFragment } from '../fragments/quest';
import {
    CreateQuestMutation,
    GetQuestsByCategoryQuery,
    GetQuestsByCategoryQueryVariables,
    GetQuestsQuery,
    GetQuestsQueryVariables,
    GetTemplateQuestsQuery,
} from '../generated/graphql';
import { getQuestsByCategoryQuery, getQuestsQuery } from '../queries/quest';
import { getTemplateQuestsQuery } from '../queries/templateQuests';
import { forEachPrimaryQuestsQuery } from './cacheHelpers';

export const createQuestMutation = gql`
    ${categoryFragment}
    ${goalFragment}
    ${questFragment}

    mutation CreateQuest(
        $name: String!
        $description: String!
        $repeat: RepeatCreateInput
        $dueDate: DateTimeISO
        $timing: Boolean!
        $timedInterval: Int!
        $timerValueInMs: Int!
        $categoryId: String!
        $difficulty: QuestDifficulty!
        $goalId: String
        $templateId: String
        $ritualType: RitualType
        $subTasks: [SubTaskCreateInput!]
    ) {
        createQuest(
            data: {
                name: $name
                description: $description
                categoryId: $categoryId
                difficulty: $difficulty
                goalId: $goalId
                repeat: $repeat
                dueDate: $dueDate
                timing: $timing
                timedInterval: $timedInterval
                timerValueInMs: $timerValueInMs
                tracking: false
                templateId: $templateId
                ritualType: $ritualType
                subTasks: $subTasks
            }
        ) {
            ...QuestFields
            category {
                ...CategoryFields
            }
            goal {
                ...GoalFields
            }
        }
    }
`;
export function createQuestCache(cache: ApolloCache<any>, { data }: { data?: CreateQuestMutation | null }) {
    if (!data?.createQuest) return;
    const newQuest = data.createQuest;

    forEachPrimaryQuestsQuery(cache, (vars, existingData) => {
        // Add the quest if it doesn't already exist
        // Note: We don't check date range because fetchMore extends the range beyond dueDateTo
        if (!existingData.quests.some(q => q.id === newQuest.id)) {
            cache.writeQuery<GetQuestsQuery, GetQuestsQueryVariables>({
                query: getQuestsQuery,
                variables: vars,
                data: {
                    ...existingData,
                    quests: [...existingData.quests, newQuest],
                },
            });
        }
    });

    const categoryData = cache.readQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
        query: getQuestsByCategoryQuery,
        variables: { categoryId: newQuest.categoryId },
    });
    if (categoryData && categoryData.questsByCategory) {
        const existingQuests = categoryData.questsByCategory;
        if (!existingQuests.some((q: any) => q.id === newQuest.id)) {
            cache.writeQuery<GetQuestsByCategoryQuery, GetQuestsByCategoryQueryVariables>({
                query: getQuestsByCategoryQuery,
                variables: { categoryId: newQuest.categoryId },
                data: { ...categoryData, questsByCategory: [...existingQuests, newQuest] },
            });
        }
    }

    if (newQuest.templateId) {
        const templateData = cache.readQuery<GetTemplateQuestsQuery>({ query: getTemplateQuestsQuery });
        if (templateData && !templateData.quests.some(q => q.id === newQuest.id)) {
            cache.writeQuery<GetTemplateQuestsQuery>({
                query: getTemplateQuestsQuery,
                data: {
                    ...templateData,
                    quests: [...templateData.quests, { id: newQuest.id, templateId: newQuest.templateId }],
                },
            });
        }
    }
}
